import React from 'react';
import { isConnectedBlockType } from '../../flow/connectedBlocks';
import { toProductCardDebugRect } from '../productCardDebugSnapshot';
import { SECTION_ROW_HEIGHT_PX } from '../layoutEngine/sectionSizing';
import type { CanvasSectionBlock } from '../sectionRegistry';

const TEXT_AUTO_HEIGHT_DEBOUNCE_MS = 240;
const DIAGNOSTIC_DISABLE_TEXT_AUTO_HEIGHT = false;

type RuntimeDiagnosticsOptions = {
  activeProjectionRows: number;
  adminPreview?: any;
  isAdmin: boolean;
  isTransforming: boolean;
  layerRef: React.RefObject<HTMLDivElement>;
  layerSize: { width: number; height: number };
  moveMode: boolean;
  renderBlocks: CanvasSectionBlock[];
  scaleMode: boolean;
  sectionId?: string;
  selectedBlockId: string | null;
  usesFixedFrameText: boolean;
};

export function useSectionElementRuntimeDiagnostics({
  activeProjectionRows,
  adminPreview,
  isAdmin,
  isTransforming,
  layerRef,
  layerSize,
  moveMode,
  renderBlocks,
  scaleMode,
  sectionId,
  selectedBlockId,
  usesFixedFrameText,
}: RuntimeDiagnosticsOptions) {
  const productCardWrapperNodesRef = React.useRef<Record<string, HTMLDivElement | null>>({});
  const productCardArticleNodesRef = React.useRef<Record<string, HTMLElement | null>>({});
  const productCardOverlayNodesRef = React.useRef<Record<string, HTMLDivElement | null>>({});
  const productCardRuntimeSnapshotsRef = React.useRef<Record<string, Record<string, unknown>>>({});
  const connectedBlockRuntimeSnapshotsRef = React.useRef<Record<string, Record<string, unknown> | null>>({});
  const lastReportedProductCardDebugSnapshotRef = React.useRef<string | null>(null);
  const lastReportedConnectedBlockDebugSnapshotRef = React.useRef<string | null>(null);
  const textMeasureNodesRef = React.useRef<Record<string, HTMLDivElement | null>>({});
  const textMeasureTimersRef = React.useRef<Record<string, number>>({});
  const productCardMeasureNodesRef = React.useRef<Record<string, HTMLElement | null>>({});
  const productCardMeasureTimersRef = React.useRef<Record<string, number>>({});
  const [textVisualRows, setTextVisualRows] = React.useState<Record<string, number>>({});
  const textVisualRowsRef = React.useRef<Record<string, number>>({});
  const [connectedBlockDebugVersion, setConnectedBlockDebugVersion] = React.useState(0);

  React.useEffect(() => {
    textVisualRowsRef.current = textVisualRows;
  }, [textVisualRows]);

  const setProductCardMeasureNode = React.useCallback((blockId: string, node: HTMLElement | null) => {
    if (node) {
      productCardMeasureNodesRef.current[blockId] = node;
    } else {
      delete productCardMeasureNodesRef.current[blockId];
    }
  }, []);

  const measureProductCardRows = React.useCallback(
    (blockId: string) => {
      if (!isAdmin || isTransforming || scaleMode) return;
      const node = productCardMeasureNodesRef.current[blockId];
      if (!node || activeProjectionRows <= 0) return;
      const sourceBlock = renderBlocks.find((block) => block.id === blockId) ?? null;
      const sourceContent =
        sourceBlock?.content && typeof sourceBlock.content === 'object'
          ? (sourceBlock.content as Record<string, unknown>)
          : {};
      const autoSizeResolved = sourceContent.autoSizeResolved !== false;
      if (moveMode && autoSizeResolved) return;
      const fallbackScale = Math.max(0.5, Math.min(1.25, Number(sourceContent.contentScale) || 1));
      const contentScaleY = Math.max(0.5, Math.min(1.25, Number(sourceContent.contentScaleY) || fallbackScale));
      const contentHeight = Math.max(1, Math.ceil(node.scrollHeight * contentScaleY));
      const rows = Math.max(1, Math.ceil(contentHeight / SECTION_ROW_HEIGHT_PX));
      adminPreview?.onBlockAutoHeightMeasured?.(blockId, rows);
    },
    [activeProjectionRows, adminPreview, isAdmin, isTransforming, moveMode, renderBlocks, scaleMode]
  );

  React.useLayoutEffect(() => {
    if (!adminPreview?.onProductCardDebugSnapshot) return;
    if (!selectedBlockId) {
      if (lastReportedProductCardDebugSnapshotRef.current === '__none__') return;
      lastReportedProductCardDebugSnapshotRef.current = '__none__';
      adminPreview.onProductCardDebugSnapshot(null, null);
      return;
    }

    const selectedBlock = renderBlocks.find((block) => block.id === selectedBlockId) ?? null;
    if (!selectedBlock || selectedBlock.type !== 'product-card') {
      const nextNullKey = `${selectedBlockId}::null`;
      if (lastReportedProductCardDebugSnapshotRef.current === nextNullKey) return;
      lastReportedProductCardDebugSnapshotRef.current = nextNullKey;
      adminPreview.onProductCardDebugSnapshot(selectedBlockId, null);
      return;
    }

    const baseSnapshot = productCardRuntimeSnapshotsRef.current[selectedBlockId] ?? null;
    const wrapperNode = productCardWrapperNodesRef.current[selectedBlockId];
    const articleNode = productCardArticleNodesRef.current[selectedBlockId];
    const overlayNode = productCardOverlayNodesRef.current[selectedBlockId];
    const layerNode = layerRef.current;
    const nextSnapshot = {
      ...(baseSnapshot ?? {}),
      dom: {
        layerRect: toProductCardDebugRect(layerNode?.getBoundingClientRect()),
        wrapperRect: toProductCardDebugRect(wrapperNode?.getBoundingClientRect()),
        articleRect: toProductCardDebugRect(articleNode?.getBoundingClientRect()),
        overlayRect: toProductCardDebugRect(overlayNode?.getBoundingClientRect()),
      },
    };
    const nextSnapshotKey = `${selectedBlockId}::${JSON.stringify(nextSnapshot)}`;
    if (lastReportedProductCardDebugSnapshotRef.current === nextSnapshotKey) return;
    lastReportedProductCardDebugSnapshotRef.current = nextSnapshotKey;
    adminPreview.onProductCardDebugSnapshot(selectedBlockId, nextSnapshot);
  }, [
    adminPreview,
    renderBlocks,
    selectedBlockId,
    scaleMode,
    activeProjectionRows,
    layerSize.width,
    layerSize.height,
  ]);

  const setConnectedBlockDebugSnapshot = React.useCallback(
    (blockId: string, snapshot: Record<string, unknown> | null) => {
      const current = connectedBlockRuntimeSnapshotsRef.current[blockId] ?? null;
      const currentSerialized = current ? JSON.stringify(current) : null;
      const nextSerialized = snapshot ? JSON.stringify(snapshot) : null;
      if (currentSerialized === nextSerialized) return;
      if (snapshot) {
        connectedBlockRuntimeSnapshotsRef.current[blockId] = snapshot;
      } else {
        delete connectedBlockRuntimeSnapshotsRef.current[blockId];
      }
      setConnectedBlockDebugVersion((version) => version + 1);
    },
    []
  );

  React.useLayoutEffect(() => {
    if (!adminPreview?.onConnectedBlockDebugSnapshot) return;
    const connectedBlocks = renderBlocks.filter((block) => isConnectedBlockType(block.type));
    if (connectedBlocks.length === 0) {
      if (lastReportedConnectedBlockDebugSnapshotRef.current === '__none__') return;
      lastReportedConnectedBlockDebugSnapshotRef.current = '__none__';
      adminPreview.onConnectedBlockDebugSnapshot(null);
      return;
    }

    const selectedConnectedBlock = connectedBlocks.find((block) => block.id === selectedBlockId) ?? null;
    const nextSnapshot = {
      sectionId: sectionId ?? null,
      selectedBlockId,
      selectedConnectedBlockId: selectedConnectedBlock?.id ?? null,
      blockCount: connectedBlocks.length,
      blocks: connectedBlocks.map((block) => ({
        blockId: block.id,
        blockType: block.type,
        selected: block.id === selectedBlockId,
        snapshot: connectedBlockRuntimeSnapshotsRef.current[block.id] ?? null,
      })),
    };
    const nextSnapshotKey = JSON.stringify(nextSnapshot);
    if (lastReportedConnectedBlockDebugSnapshotRef.current === nextSnapshotKey) return;
    lastReportedConnectedBlockDebugSnapshotRef.current = nextSnapshotKey;
    adminPreview.onConnectedBlockDebugSnapshot(nextSnapshot);
  }, [adminPreview, connectedBlockDebugVersion, renderBlocks, sectionId, selectedBlockId]);

  const setTextMeasureNode = React.useCallback((blockId: string, node: HTMLDivElement | null) => {
    if (node) {
      textMeasureNodesRef.current[blockId] = node;
    } else {
      delete textMeasureNodesRef.current[blockId];
    }
  }, []);

  const measureTextBlockRows = React.useCallback(
    (blockId: string) => {
      if (DIAGNOSTIC_DISABLE_TEXT_AUTO_HEIGHT) return;
      if (usesFixedFrameText) return;
      if (!isAdmin || isTransforming) return;
      const node = textMeasureNodesRef.current[blockId];
      if (!node || activeProjectionRows <= 0) return;
      const contentHeight = Math.max(1, Math.ceil(node.scrollHeight));
      const rows = Math.max(1, Math.min(activeProjectionRows, Math.ceil(contentHeight / SECTION_ROW_HEIGHT_PX)));
      setTextVisualRows((prev) => (prev[blockId] === rows ? prev : { ...prev, [blockId]: rows }));
      adminPreview?.onTextAutoHeightMeasured?.(blockId, rows);
    },
    [activeProjectionRows, adminPreview, isAdmin, isTransforming, usesFixedFrameText]
  );

  React.useEffect(() => {
    if (DIAGNOSTIC_DISABLE_TEXT_AUTO_HEIGHT || usesFixedFrameText || !isAdmin || typeof ResizeObserver === 'undefined') {
      if (Object.keys(textVisualRowsRef.current).length > 0) {
        textVisualRowsRef.current = {};
        setTextVisualRows({});
      }
      return;
    }
    const textBlockIds = renderBlocks.filter((block) => block.type === 'text').map((block) => block.id);
    const allowedIds = new Set(textBlockIds);
    const currentTextVisualRows = textVisualRowsRef.current;
    const hasStaleTextVisualRows = Object.keys(currentTextVisualRows).some((key) => !allowedIds.has(key));
    if (hasStaleTextVisualRows) {
      const nextTextVisualRows = Object.fromEntries(
        Object.entries(currentTextVisualRows).filter(([key]) => allowedIds.has(key))
      );
      textVisualRowsRef.current = nextTextVisualRows;
      setTextVisualRows(nextTextVisualRows);
    }

    const scheduleMeasure = (blockId: string) => {
      const existingTimer = textMeasureTimersRef.current[blockId];
      if (existingTimer) window.clearTimeout(existingTimer);
      textMeasureTimersRef.current[blockId] = window.setTimeout(() => {
        delete textMeasureTimersRef.current[blockId];
        measureTextBlockRows(blockId);
      }, TEXT_AUTO_HEIGHT_DEBOUNCE_MS);
    };

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const blockId = (entry.target as HTMLElement).dataset.builderTextMeasureBlockId;
        if (!blockId || !allowedIds.has(blockId)) continue;
        if (!isTransforming) scheduleMeasure(blockId);
      }
    });

    for (const blockId of textBlockIds) {
      const node = textMeasureNodesRef.current[blockId];
      if (!node) continue;
      node.dataset.builderTextMeasureBlockId = blockId;
      observer.observe(node);
      if (!isTransforming) scheduleMeasure(blockId);
    }

    return () => {
      observer.disconnect();
      for (const blockId of textBlockIds) {
        const timerId = textMeasureTimersRef.current[blockId];
        if (timerId) {
          window.clearTimeout(timerId);
          delete textMeasureTimersRef.current[blockId];
        }
      }
    };
  }, [isAdmin, isTransforming, measureTextBlockRows, renderBlocks, usesFixedFrameText]);

  React.useEffect(() => {
    if (!isAdmin || scaleMode || typeof ResizeObserver === 'undefined') return;
    const productCardIds = renderBlocks.filter((block) => block.type === 'product-card').map((block) => block.id);
    const allowedIds = new Set(productCardIds);

    const scheduleMeasure = (blockId: string) => {
      const existingTimer = productCardMeasureTimersRef.current[blockId];
      if (existingTimer) window.clearTimeout(existingTimer);
      productCardMeasureTimersRef.current[blockId] = window.setTimeout(() => {
        delete productCardMeasureTimersRef.current[blockId];
        measureProductCardRows(blockId);
      }, TEXT_AUTO_HEIGHT_DEBOUNCE_MS);
    };

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const blockId = (entry.target as HTMLElement).dataset.builderProductCardMeasureBlockId;
        if (!blockId || !allowedIds.has(blockId) || isTransforming) continue;
        scheduleMeasure(blockId);
      }
    });

    for (const blockId of productCardIds) {
      const node = productCardMeasureNodesRef.current[blockId];
      if (!node) continue;
      node.dataset.builderProductCardMeasureBlockId = blockId;
      observer.observe(node);
      if (!isTransforming) scheduleMeasure(blockId);
    }

    return () => {
      observer.disconnect();
      for (const blockId of productCardIds) {
        const timerId = productCardMeasureTimersRef.current[blockId];
        if (timerId) {
          window.clearTimeout(timerId);
          delete productCardMeasureTimersRef.current[blockId];
        }
      }
    };
  }, [isAdmin, isTransforming, measureProductCardRows, moveMode, renderBlocks, scaleMode, usesFixedFrameText]);

  return {
    productCardWrapperNodesRef,
    productCardArticleNodesRef,
    productCardOverlayNodesRef,
    productCardRuntimeSnapshotsRef,
    setConnectedBlockDebugSnapshot,
    setTextMeasureNode,
    setProductCardMeasureNode,
  };
}
